import { z } from 'zod';
// Base schemas
export const userRoleSchema = z.enum(['owner', 'manager', 'cashier']);
export const scheduleTypeSchema = z.enum(['weekly', 'bi-weekly', 'monthly']);
export const paymentMethodSchema = z.enum(['cash', 'transfer']);
export const reminderTypeSchema = z.enum(['pre', 'due', 'post', 'promise']);
export const reminderChannelSchema = z.enum(['wa', 'sms']);
export const installmentStatusSchema = z.enum(['pending', 'paid', 'overdue', 'cancelled']);
export const agreementStatusSchema = z.enum(['active', 'completed', 'cancelled', 'defaulted']);
// Shop schema
export const shopSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(100),
    currency: z.string().default('LYD'),
    timezone: z.string().default('Africa/Tripoli'),
    owner_id: z.string().uuid(),
    created_at: z.date(),
    updated_at: z.date(),
});
// Branch schema
export const branchSchema = z.object({
    id: z.string().uuid(),
    shop_id: z.string().uuid(),
    name: z.string().min(1).max(100),
    phone: z.string().regex(/^\+?[1-9]\d{1,14}$/),
    created_at: z.date(),
    updated_at: z.date(),
});
// User schema
export const userSchema = z.object({
    id: z.string().uuid(),
    shop_id: z.string().uuid(),
    role: userRoleSchema,
    name: z.string().min(1).max(100),
    phone: z.string().regex(/^\+?[1-9]\d{1,14}$/),
    email: z.string().email().optional(),
    status: z.enum(['active', 'inactive']).default('active'),
    created_at: z.date(),
    updated_at: z.date(),
});
// Customer schema
export const customerSchema = z.object({
    id: z.string().uuid(),
    shop_id: z.string().uuid(),
    name: z.string().min(1).max(100),
    phone: z.string().regex(/^\+?[1-9]\d{1,14}$/),
    alt_phone: z.string().regex(/^\+?[1-9]\d{1,14}$/).optional(),
    address: z.string().max(500).optional(),
    id_type: z.string().max(50).optional(),
    id_number: z.string().max(50).optional(),
    id_images: z.array(z.string().url()),
    consent_opt_in_at: z.date().optional(),
    created_at: z.date(),
    updated_at: z.date(),
});
// Agreement schema
export const agreementSchema = z.object({
    id: z.string().uuid(),
    shop_id: z.string().uuid(),
    branch_id: z.string().uuid(),
    customer_id: z.string().uuid(),
    item_desc: z.string().min(1).max(500),
    serial: z.string().max(100).optional(),
    total_price: z.number().positive(),
    deposit: z.number().min(0),
    admin_fee_total: z.number().min(0),
    schedule_type: scheduleTypeSchema,
    installment_count: z.number().int().positive(),
    due_day: z.number().int().min(1).max(31),
    grace_days: z.number().int().min(0).max(30),
    penalty_rule_json: z.string(),
    start_date: z.date(),
    status: agreementStatusSchema,
    signed_at: z.date().optional(),
    signature_img: z.string().url().optional(),
    created_at: z.date(),
    updated_at: z.date(),
});
// Installment schema
export const installmentSchema = z.object({
    id: z.string().uuid(),
    agreement_id: z.string().uuid(),
    seq: z.number().int().positive(),
    due_date: z.date(),
    amount_due: z.number().positive(),
    amount_paid: z.number().min(0),
    status: installmentStatusSchema,
    penalty_accrued: z.number().min(0),
    paid_at: z.date().optional(),
    created_at: z.date(),
    updated_at: z.date(),
});
// Payment schema
export const paymentSchema = z.object({
    id: z.string().uuid(),
    agreement_id: z.string().uuid(),
    installment_id: z.string().uuid().optional(),
    branch_id: z.string().uuid(),
    method: paymentMethodSchema,
    amount: z.number().positive(),
    cashier_id: z.string().uuid(),
    created_at: z.date(),
    note: z.string().max(500).optional(),
});
// Receipt schema
export const receiptSchema = z.object({
    id: z.string().uuid(),
    payment_id: z.string().uuid(),
    pdf_url: z.string().url(),
    short_code: z.string().length(6),
    created_at: z.date(),
});
// Reminder schema
export const reminderSchema = z.object({
    id: z.string().uuid(),
    agreement_id: z.string().uuid(),
    installment_id: z.string().uuid(),
    type: reminderTypeSchema,
    channel: reminderChannelSchema,
    scheduled_at: z.date(),
    sent_at: z.date().optional(),
    status: z.enum(['pending', 'sent', 'failed']),
    created_at: z.date(),
});
// Driver Intake schema
export const driverIntakeSchema = z.object({
    id: z.string().uuid(),
    branch_id: z.string().uuid(),
    driver_name: z.string().min(1).max(100),
    expected_total: z.number().min(0),
    received_total: z.number().min(0),
    variance: z.number(),
    closed_at: z.date().optional(),
    note: z.string().max(500).optional(),
    created_at: z.date(),
});
// Audit Log schema
export const auditLogSchema = z.object({
    id: z.string().uuid(),
    shop_id: z.string().uuid(),
    user_id: z.string().uuid(),
    action: z.string().min(1).max(100),
    entity: z.string().min(1).max(50),
    entity_id: z.string().uuid(),
    meta_json: z.string(),
    created_at: z.date(),
});
// Input schemas (for API requests)
export const createAgreementInputSchema = z.object({
    customer_id: z.string().uuid(),
    item_desc: z.string().min(1).max(500),
    serial: z.string().max(100).optional(),
    total_price: z.number().positive(),
    deposit: z.number().min(0),
    admin_fee_total: z.number().min(0),
    schedule_type: scheduleTypeSchema,
    installment_count: z.number().int().positive(),
    due_day: z.number().int().min(1).max(31),
    grace_days: z.number().int().min(0).max(30),
    penalty_rule_json: z.string(),
    start_date: z.string().datetime(),
});
export const createPaymentInputSchema = z.object({
    agreement_id: z.string().uuid(),
    installment_id: z.string().uuid().optional(),
    method: paymentMethodSchema,
    amount: z.number().positive(),
    note: z.string().max(500).optional(),
});
export const createCustomerInputSchema = z.object({
    name: z.string().min(1).max(100),
    phone: z.string().regex(/^\+?[1-9]\d{1,14}$/),
    alt_phone: z.string().regex(/^\+?[1-9]\d{1,14}$/).optional(),
    address: z.string().max(500).optional(),
    id_type: z.string().max(50).optional(),
    id_number: z.string().max(50).optional(),
    consent_opt_in: z.boolean().default(false),
});
//# sourceMappingURL=schemas.js.map